import { supabase } from './supabase';

export type ChannelVisibility = 'public' | 'private' | 'internal';

export interface ChannelRow {
  id: string;
  slug: string;
  label: string;
  description: string | null;
  url: string | null;
  visibility: ChannelVisibility;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export function mapChannelRow(raw: Record<string, unknown>): ChannelRow {
  return {
    id: String(raw.id),
    slug: String(raw.slug ?? ''),
    label: String(raw.label ?? ''),
    description: raw.description != null ? String(raw.description) : null,
    url: raw.url != null && String(raw.url).trim() ? String(raw.url).trim() : null,
    visibility: (raw.visibility as ChannelVisibility) ?? 'public',
    sort_order: Number(raw.sort_order ?? 0),
    active: Boolean(raw.active ?? true),
    created_at: String(raw.created_at ?? ''),
    updated_at: String(raw.updated_at ?? ''),
  };
}

export function sortChannels(rows: ChannelRow[]): ChannelRow[] {
  return [...rows].sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
}

export function channelKeyFromToken(token: string): string {
  return token
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function isValidChannelSlug(slug: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(slug) && slug.length >= 2 && slug.length <= 64;
}

/** Normalize subscriber boolean map or newsletter slug array into a slug list. */
export function channelsToKeyArray(channels: unknown): string[] {
  if (channels == null) return [];
  if (Array.isArray(channels)) {
    return channels
      .map((s) => (typeof s === 'string' ? s.trim() : ''))
      .filter(Boolean)
      .map(channelKeyFromToken);
  }
  if (typeof channels === 'string') {
    return channels
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map(channelKeyFromToken);
  }
  if (typeof channels === 'object') {
    const o = channels as Record<string, unknown>;
    return Object.entries(o)
      .filter(
        ([, v]) =>
          v === true ||
          (typeof v === 'string' && /^(true|1|yes)$/i.test(v.trim())) ||
          (typeof v === 'number' && v !== 0),
      )
      .map(([k]) => channelKeyFromToken(k))
      .filter(Boolean);
  }
  return [];
}

/** @alias channelsToKeyArray — used for newsletter `channels` jsonb arrays. */
export const channelsToArray = channelsToKeyArray;

export function channelsMapFromKeys(keys: string[]): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const k of keys) {
    const slug = channelKeyFromToken(k);
    if (slug) map[slug] = true;
  }
  return map;
}

export function channelLabel(slug: string, catalog?: ChannelRow[]): string {
  const trimmed = slug.trim();
  const known = catalog?.find((c) => c.slug === trimmed);
  if (known) return known.label;
  return trimmed
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function visibilityLabel(v: ChannelVisibility): string {
  if (v === 'public') return 'Public';
  if (v === 'private') return 'Private';
  return 'Internal';
}

/** Count subscribers per channel slug from subscriber rows. */
export function countSubscribersByChannel(rows: Array<{ channels: unknown }>): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const row of rows) {
    for (const slug of channelsToKeyArray(row.channels)) {
      counts[slug] = (counts[slug] ?? 0) + 1;
    }
  }
  return counts;
}

/** Load subscriber counts keyed by channel slug (all connections with that channel flag). */
export async function fetchSubscriberCountsByChannel(): Promise<Record<string, number>> {
  const { data, error } = await supabase.from('subscribers').select('channels').limit(10000);
  if (error) throw error;
  return countSubscribersByChannel((data ?? []) as Array<{ channels: unknown }>);
}

export async function fetchAllChannels(): Promise<ChannelRow[]> {
  const { data, error } = await supabase.from('channels').select('*').order('sort_order', { ascending: true });
  if (error) throw error;
  return sortChannels((data ?? []).map((r) => mapChannelRow(r as Record<string, unknown>)));
}

export async function fetchActiveChannels(): Promise<ChannelRow[]> {
  const { data, error } = await supabase
    .from('channels')
    .select('*')
    .eq('active', true)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return sortChannels((data ?? []).map((r) => mapChannelRow(r as Record<string, unknown>)));
}

/** Merge catalog slugs with discovered keys from subscriber data. */
export function withCatalogChannels(discovered: Iterable<string>, catalog: ChannelRow[]): string[] {
  const set = new Set<string>();
  for (const c of catalog) {
    if (c.active) set.add(c.slug);
  }
  for (const k of discovered) {
    const trimmed = typeof k === 'string' ? k.trim() : '';
    if (trimmed) set.add(channelKeyFromToken(trimmed));
  }
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}
